import React, { useCallback, useEffect, useState } from 'react';
import { useIsFocused } from '@react-navigation/native';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { EDGE_ONLY_GESTURE, PLEINE_LARGEUR, STACK_SCREEN_OPTIONS } from './screenOptions';
import { SUR_LE_WEB } from '../theme/tokens';
import { useDiscardGuard } from '../hooks/useDiscardGuard';
import { NouveautesModal } from '../components/NouveautesModal';
import { useNouveautesStore } from '../store/nouveautesStore';
import { usePaywallStore } from '../store/paywallStore';
import { CarriereScreen } from '../screens/CarriereScreen';
import { MyCvScreen } from '../screens/MyCvScreen';
import { MyCoverLetterScreen } from '../screens/MyCoverLetterScreen';
import { CoverLetterCreateScreen } from '../screens/CoverLetterCreateScreen';
import { CoverLetterListScreen } from '../screens/CoverLetterListScreen';
import { CoverLetterAnalyseScreen, type AmeliorationLettreParams } from '../screens/CoverLetterAnalyseScreen';
import { CoverLetterImproveScreen } from '../screens/CoverLetterImproveScreen';
import { LinkedinAuditScreen } from '../screens/LinkedinAuditScreen';
import { InterviewScreen } from '../screens/InterviewScreen';
import {
  CvScoreScreen,
  type CvScoreInitialOffer,
  type CvImproveSource,
} from '../screens/CvScoreScreen';
import { CvCreateScreen, type CvCreateInitial } from '../screens/CvCreateScreen';
import { CvImproveScreen } from '../screens/CvImproveScreen';
import { CvListScreen } from '../screens/CvListScreen';
import type { PickedFile } from '../services/mediaPicker';
import type { SavedCoverLetter } from '../types';
import { DEFAULT_TEMPLATE } from '../theme/templates';

// Le nom de route `CvHub` est CONSERVÉ bien que l'écran s'appelle Carrière : le renommer imposerait
// de reprendre chaque `navigate('CVs', { screen: … })`, faiblement typé, sans que tsc le voie.
// CvHub reste la route INITIALE de la pile, et cette pile est désormais l'onglet d'atterrissage
// après connexion : c'est Carrière que l'on voit en arrivant dans l'application.
export type CvStackParamList = {
  CvHub: undefined;
  MyCv: undefined;
  LinkedinAudit: undefined;
  CvScore:
    | { initialCvText?: string; initialOffer?: CvScoreInitialOffer; initialFile?: PickedFile }
    | undefined;
  CvCreate: { initial?: CvCreateInitial } | undefined;
  CvImprove: { cv: CvImproveSource };
  CvList: undefined;
  MyCoverLetter: undefined;
  CoverLetterCreate: { letter?: SavedCoverLetter } | undefined;
  CoverLetterList: undefined;
  CoverLetterAnalyse: { letter?: SavedCoverLetter } | undefined;
  CoverLetterImprove: AmeliorationLettreParams;
  // L'entretien était une RACINE d'onglet ; il devient une entrée de Carrière comme les
  // autres features. Il porte son propre état de session (useInterview), non enregistré,
  // mais ChatView garde déjà sa confirmation d'abandon : pas de useDiscardGuard ici.
  Interview: undefined;
};

const Stack = createNativeStackNavigator<CvStackParamList>();

const DISCARD_MESSAGE =
  'Ce résultat n’est pas enregistré. Si tu quittes, il faudra relancer une analyse et ta limite mensuelle sera de nouveau décomptée.';

const WIZARD_MESSAGE =
  'Ta saisie n’est pas enregistrée. Si tu quittes maintenant, tu devras tout ressaisir.';

// La garde doit être appelée dans l'arbre de l'écran de pile pour viser la bonne route. On enveloppe
// donc l'écran plutôt que d'appeler le hook dedans : les écrans restent testables hors navigateur.
function GuardedLinkedinAudit({ onBack }: { onBack: () => void }) {
  const [hasResult, setHasResult] = useState(false);
  useDiscardGuard(hasResult, DISCARD_MESSAGE);
  return <LinkedinAuditScreen onBack={onBack} onResultChange={setHasResult} />;
}

// Un CV importé est enregistré AVEC sa note et ses conseils : la note n'est plus un résultat perdu,
// et la garde ne retient plus la sortie. Une nouvelle note la réarme.
function GuardedCvScore({ onSaveImported, ...props }: NonNullable<React.ComponentProps<typeof CvScoreScreen>>) {
  const [hasResult, setHasResult] = useState(false);
  const [importEnregistre, setImportEnregistre] = useState(false);
  useDiscardGuard(hasResult && !importEnregistre, DISCARD_MESSAGE);
  // Stable : l'écran l'a dans les dépendances d'un effet, une nouvelle fonction à chaque rendu
  // effacerait aussitôt `importEnregistre`.
  const surResultat = useCallback((resultat: boolean) => {
    setHasResult(resultat);
    setImportEnregistre(false);
  }, []);
  return (
    <CvScoreScreen
      {...props}
      onResultChange={surResultat}
      onSaveImported={(cvId, cv) => {
        setImportEnregistre(true);
        onSaveImported?.(cvId, cv);
      }}
    />
  );
}

// Wizards : la garde ne s'arme que si la saisie a réellement commencé (cf. `dirty` dans chaque
// écran). Un formulaire vierge ne déclenche rien — une alerte au premier écran serait pénible.
//
// « Enregistrer » : marquer enregistré D'ABORD, naviguer ENSUITE. La garde lit son état au dernier
// rendu ; naviguer dans le même tour que l'enregistrement la trouvait encore armée, et la fenêtre de
// sortie s'ouvrait au lieu de la liste (retour d'essai du 17/10/2026).
function GuardedCvCreate({ onSaved, ...props }: NonNullable<React.ComponentProps<typeof CvCreateScreen>>) {
  const [dirty, setDirty] = useState(false);
  const [enregistre, setEnregistre] = useState(false);
  useDiscardGuard(dirty && !enregistre, WIZARD_MESSAGE);
  useEffect(() => {
    if (enregistre) onSaved?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enregistre]);
  return <CvCreateScreen {...props} onDirtyChange={setDirty} onSaved={() => setEnregistre(true)} />;
}

function GuardedCoverLetterImprove(props: React.ComponentProps<typeof CoverLetterImproveScreen>) {
  const [dirty, setDirty] = useState(false);
  useDiscardGuard(dirty, WIZARD_MESSAGE);
  return <CoverLetterImproveScreen {...props} onDirtyChange={setDirty} />;
}

function GuardedCvImprove(props: React.ComponentProps<typeof CvImproveScreen>) {
  const [dirty, setDirty] = useState(false);
  useDiscardGuard(dirty, WIZARD_MESSAGE);
  return <CvImproveScreen {...props} onDirtyChange={setDirty} />;
}

/**
 * L'ACCUEIL, SEUL ENDROIT OÙ LES NOUVEAUTÉS PEUVENT APPARAÎTRE.
 *
 * Monté sous `Main`, il n'existe pas tant qu'un écran bloquant de mise à jour, l'acceptation des
 * conditions ou la complétion du profil sont affichés. `useIsFocused` l'écarte quand un entretien,
 * le coaching ou un autre onglet est au premier plan ; le paywall ouvert l'écarte aussi. La fenêtre
 * attend : elle apparaît au premier retour sur l'accueil.
 */
function AccueilAvecNouveautes({
  onVoirOffres,
  onVoirPolitique,
  ...props
}: React.ComponentProps<typeof CarriereScreen> & { onVoirOffres: () => void; onVoirPolitique: () => void }) {
  const auPremierPlan = useIsFocused();
  const { enAttente, versionAnnoncee, politiqueAnnoncee, fermer } = useNouveautesStore();
  const paywallOuvert = usePaywallStore((s) => s.visible);
  return (
    <>
      <CarriereScreen {...props} />
      <NouveautesModal
        visible={enAttente && auPremierPlan && !paywallOuvert}
        onFermer={() => void fermer()}
        onVoirOffres={() => {
          void fermer();
          onVoirOffres();
        }}
        versionAnnoncee={versionAnnoncee}
        politiqueAnnoncee={politiqueAnnoncee}
        onVoirPolitique={() => {
          void fermer();
          onVoirPolitique();
        }}
      />
    </>
  );
}

export function CvNavigator() {
  return (
    <Stack.Navigator screenOptions={STACK_SCREEN_OPTIONS}>
      {/* SEUL ÉCRAN PLEINE LARGEUR DE L'APPLICATION, et seulement sur le web.

          Carrière a besoin que son carrousel et son décor touchent les deux bords de la fenêtre :
          bornés à 1200, la carte suivante et le halo étaient tranchés net à la verticale, avec du
          fond nu au-delà. L'échappée est posée ICI, sur ce seul écran, et NON dans
          `STACK_SCREEN_OPTIONS.contentStyle` — le desserrer là déborderait sur les six piles et
          referait les marges irrégulières qu'on vient de fermer.

          L'écran reborne lui-même ce qui doit rester lu en colonne (son titre). Sur mobile la prop
          reste `undefined` : la borne partagée s'applique comme avant, au pixel près. */}
      <Stack.Screen name="CvHub" options={SUR_LE_WEB ? PLEINE_LARGEUR : undefined}>
        {({ navigation }) => (
          <AccueilAvecNouveautes
            // L'onglet Offres appartient à la barre du bas, parente de cette pile.
            onVoirOffres={() => navigation.getParent()?.navigate('Offers')}
            // L'écran de la politique ouvert depuis les réglages ; `initial: false` garde l'accueil du profil dessous.
            onVoirPolitique={() => navigation.getParent()?.navigate('Profile', { screen: 'Privacy', initial: false })}
            onOpenMyCv={() => navigation.navigate('MyCv')}
            onOpenLinkedin={() => navigation.navigate('LinkedinAudit')}
            onOpenLetter={() => navigation.navigate('MyCoverLetter')}
            onOpenInterview={() => navigation.navigate('Interview')}
          />
        )}
      </Stack.Screen>
      <Stack.Screen name="MyCv">
        {({ navigation }) => (
          <MyCvScreen
            onBack={() => navigation.goBack()}
            onOpenScore={() => navigation.navigate('CvScore')}
            onImportCv={(file) => navigation.navigate('CvScore', { initialFile: file })}
            onOpenCreate={() => navigation.navigate('CvCreate')}
            onOpenImprove={(file) => navigation.navigate('CvImprove', { cv: { file } })}
            onOpenList={() => navigation.navigate('CvList')}
          />
        )}
      </Stack.Screen>
      <Stack.Screen name="LinkedinAudit">
        {({ navigation }) => <GuardedLinkedinAudit onBack={() => navigation.goBack()} />}
      </Stack.Screen>
      <Stack.Screen name="CvScore">
        {({ route, navigation }) => (
          <GuardedCvScore
            onBack={() => navigation.goBack()}
            initialCvText={route.params?.initialCvText}
            initialOffer={route.params?.initialOffer}
            initialFile={route.params?.initialFile}
            onImprove={(cv) => navigation.navigate('CvImprove', { cv })}
            // Extraction partielle assumée : on ouvre l'éditeur sur le CV enregistré pour que la
            // personne complète ce qui manque, au lieu de la laisser devant une fiche trouée.
            onSaveImported={(cvId, cv) =>
              navigation.navigate('CvCreate', {
                initial: {
                  cvId,
                  cv,
                  title: cv.headline.trim() || 'CV importé',
                  template: DEFAULT_TEMPLATE,
                  accentColor: null,
                  edit: true,
                },
              })
            }
          />
        )}
      </Stack.Screen>
      <Stack.Screen name="CvImprove">
        {({ route, navigation }) => (
          <GuardedCvImprove
            cv={route.params.cv}
            onBack={() => navigation.goBack()}
            onDone={(initial) => navigation.navigate('CvCreate', { initial })}
          />
        )}
      </Stack.Screen>
      <Stack.Screen name="CvCreate" options={EDGE_ONLY_GESTURE}>
        {({ route, navigation }) => (
          <GuardedCvCreate
            initial={route.params?.initial}
            onBack={() => navigation.goBack()}
            // La liste déjà ouverte en dessous (CV rouvert depuis elle) est rejointe ; sinon elle
            // remplace l'éditeur. Les écrans plus bas restent intacts : leur garde n'est pas sollicitée.
            onSaved={() => navigation.popTo('CvList')}
            // Carte « Tu as déjà un CV ? » : l'import existe déjà dans Note mon CV (dépôt, note, puis « Enregistrer »).
            onImportCv={() => navigation.navigate('CvScore')}
            onTestInScore={({ cvText, offer }) =>
              navigation.navigate('CvScore', { initialCvText: cvText, initialOffer: offer })
            }
          />
        )}
      </Stack.Screen>
      <Stack.Screen name="MyCoverLetter">
        {({ navigation }) => (
          <MyCoverLetterScreen
            onBack={() => navigation.goBack()}
            onOpenCreate={() => navigation.navigate('CoverLetterCreate')}
            onOpenAnalyse={() => navigation.navigate('CoverLetterAnalyse')}
            onOpenList={() => navigation.navigate('CoverLetterList')}
            onOpenLetter={(letter) => navigation.navigate('CoverLetterCreate', { letter })}
          />
        )}
      </Stack.Screen>
      <Stack.Screen name="CoverLetterAnalyse">
        {({ navigation, route }) => (
          <CoverLetterAnalyseScreen
            onBack={() => navigation.goBack()}
            letter={route.params?.letter}
            onImprove={(params) => navigation.navigate('CoverLetterImprove', params)}
          />
        )}
      </Stack.Screen>
      <Stack.Screen name="CoverLetterImprove">
        {({ route, navigation }) => (
          <GuardedCoverLetterImprove
            {...route.params}
            onBack={() => navigation.goBack()}
            onDone={(letter) => navigation.navigate('CoverLetterCreate', { letter })}
          />
        )}
      </Stack.Screen>

      <Stack.Screen name="CoverLetterList">
        {({ navigation }) => (
          <CoverLetterListScreen
            onBack={() => navigation.goBack()}
            onCreate={() => navigation.navigate('CoverLetterCreate')}
            onOpenLetter={(letter) => navigation.navigate('CoverLetterCreate', { letter })}
          />
        )}
      </Stack.Screen>
      <Stack.Screen name="CoverLetterCreate">
        {({ route, navigation }) => (
          <CoverLetterCreateScreen
            onBack={() => navigation.goBack()}
            onOpenMyCv={() => navigation.navigate('MyCv')}
            initialLetter={route.params?.letter}
          />
        )}
      </Stack.Screen>
      <Stack.Screen name="Interview">
        {({ navigation }) => (
          <InterviewScreen
            onBack={() => navigation.goBack()}
            onAddCv={() => navigation.navigate('MyCv')}
          />
        )}
      </Stack.Screen>
      <Stack.Screen name="CvList">
        {({ navigation }) => (
          <CvListScreen
            onOpenCv={(initial) => navigation.navigate('CvCreate', { initial })}
            onCreate={() => navigation.navigate('CvCreate')}
            onBack={() => navigation.goBack()}
          />
        )}
      </Stack.Screen>
    </Stack.Navigator>
  );
}
